import {
	finalize, Observable, of, tap,
} from 'rxjs';

import {
	AuthService,
	Container,
	history,
	Service,
} from '../../../../symphony';

import { SlApiService } from '../sl-api.service';
import { SlDataService } from '../sl-data.service';
import {
	HOT_TODAY_QUESTIONS_COUNT,
	QUESTIONS_LIST_COUNT,
	questionsFilerTypeAlias,
	SlDiscussContext,
} from '../../global-constants';
import { ISelectedTags, QuestionPageType } from '../../global-interface';
import { IFlashMessageTypes } from '../../../../shared/public/SlFlashMessage/SlFlashMessage';
import { getInitialServerSideData } from '../../../../shared/public/SlUtils/getInitialServerSideData';
import {
	IDiscussPostData, IDiscussDetailsResponseDataType, DiscussSearchFilter, IPostQuestionPayload, IDiscussAddReplyResponseData, IGetTagsResponse,
} from '../../../../api/public/discuss-api';

@Service()
export class SlQuestionService {
	private api = Container.take(SlDiscussContext, SlApiService);

	private dataService = Container.take(SlDiscussContext, SlDataService);

	private authService = Container.take('global', AuthService);

	private setIsMyQuestion = () => {
		const isMyQuestion = this.dataService.question$.value?.userID
			=== this.authService.getUser()?.id;

		this.dataService.isMyQuestion$.next(isMyQuestion);
	};

	public getDiscussQuestion = (
		id: string,
		type?: QuestionPageType,
	): Observable<IDiscussPostData> => {
		const ssrQuestion = getInitialServerSideData('ssrQuestion');

		if (ssrQuestion) {
			this.dataService.question$.next(ssrQuestion.post);

			return of(null);
		}

		return this.api.getDiscussQuestion(id).pipe(
			tap((data) => {
				if (data.post) {
					this.dataService.question$.next(data.post);
					this.setIsMyQuestion();
					return of(null);
				}

				if (type === QuestionPageType.Edit) {
					this.dataService.isQuestionNotExist$.next(true);
					return of(null);
				}

				history.replace('/pagenotfound');

				return of(null);
			}),
		);
	};

	public triggerNotificationVisibility = (): Observable<null> => {
		this.dataService.notificationData$.next(null);

		return of(null);
	};

	public getHotTodayTopics = (): Observable<IDiscussDetailsResponseDataType> => {
		const ssrHotTodayQuestions = getInitialServerSideData(
			'ssrHotTodayQuestions',
		);

		if (ssrHotTodayQuestions) {
			this.dataService.hotTodayTopics$.next(ssrHotTodayQuestions);

			return of(null);
		}

		return this.api
			.getDiscussDetails({
				count: HOT_TODAY_QUESTIONS_COUNT,
				filter: DiscussSearchFilter.HotToday,
				index: 0,
				query: '',
			})
			.pipe(
				tap((hotTodayTopics) => {
					this.dataService.hotTodayTopics$.next(
						hotTodayTopics.posts,
					);
				}),
			);
	};

	public postQuestion = (
		payload: IPostQuestionPayload,
	): Observable<IDiscussAddReplyResponseData> => {
		this.dataService.postQuestionLoading$.next(true);
		return this.api.postQuestion(payload).pipe(
			tap((data) => {
				if (data?.error?.data === 2) {
					this.dataService.notificationData$.next({
						message:
							'Please activate your account to perform this action. Activation instructions have been sent to your email.',
						type: IFlashMessageTypes.ERROR,
					});
					return of(null);
				}
				this.dataService.isQuestionPosted$.next(true);
				this.dataService.showPostQuestionSuccessModal$.next(true);
				return of(null);
			}),
			finalize(() => {
				this.dataService.postQuestionLoading$.next(false);
			}),
		);
	};

	public editQuestion = (
		payload: IPostQuestionPayload,
	): Observable<IDiscussPostData> => {
		this.dataService.postQuestionLoading$.next(true);
		return this.api
			.editQuestion(payload, this.dataService.question$.value.id)
			.pipe(
				tap(() => {
					this.dataService.isQuestionEdited$.next(true);
					this.dataService.notificationData$.next({
						type: IFlashMessageTypes.SUCCESS,
						message: 'You have successfully edited your post!',
					});
					return of(null);
				}),
				finalize(() => {
					this.dataService.postQuestionLoading$.next(false);
				}),
			);
	};

	public deleteQuestion = (): Observable<object> => {
		this.dataService.deleteQuestionLoading$.next(true);
		return this.api
			.deleteQuestion(this.dataService.question$.value.id)
			.pipe(
				tap(() => {
					this.dataService.showDeleteQuestionModal$.next(false);
					this.dataService.question$.next(null);
					this.dataService.isQuestionDeleted$.next(true);
					return of(null);
				}),
				finalize(() => {
					this.dataService.deleteQuestionLoading$.next(false);
				}),
			);
	};

	public getTags = (tagInput: string): Observable<IGetTagsResponse> => this.api.getTags(tagInput).pipe(
		tap((res) => {
			const modifiedOptions: ISelectedTags[] = res.tags.map(
				(tag) => ({
					label: tag,
					value: tag,
				}),
			);
			this.dataService.suggestedTags$.next(modifiedOptions);
			return of(null);
		}),
	);

	public getQuestionsList = (
		questionsListSearchText: string,
		questionsListFilter: string,
		currentPageNumber: number,
	): Observable<IDiscussDetailsResponseDataType> => {
		const ssrQuestionsList = getInitialServerSideData('ssrQuestionsList');
		const ssrQuestionsListCount = getInitialServerSideData(
			'ssrQuestionsListCount',
		);
		const discussFilter = DiscussSearchFilter[
			Object.keys(questionsFilerTypeAlias).find(
				(key) => questionsFilerTypeAlias[key] === questionsListFilter,
			)
		];

		if (ssrQuestionsList) {
			this.dataService.questionsList$.next(ssrQuestionsList);
			this.dataService.questionsListCount$.next(ssrQuestionsListCount);

			return of(null);
		}

		this.dataService.isQuestionsListLoading$.next(true);
		return this.api
			.getDiscussDetails({
				count: QUESTIONS_LIST_COUNT,
				filter: discussFilter,
				index: QUESTIONS_LIST_COUNT * (currentPageNumber - 1),
				query: questionsListSearchText,
				profileId: this.authService.getUser()?.id || 0,
			})
			.pipe(
				tap((questions) => {
					this.dataService.questionsList$.next(questions.posts);
					this.dataService.questionsListCount$.next(questions.count);
				}),
				finalize(() => {
					this.dataService.isQuestionsListLoading$.next(false);
				}),
			);
	};
}
